import type { BlockCodeData } from '@gitbook/doc-core';
import {
    createPalette,
    listCodeBlockSyntaxes,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
} from '@gitbook/react-uikit';
import { type EditorContainer, useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { filterOutNullable } from '@gitbook/util-ts';

import { useBlockLayoutGroupFactory, useBlockPaletteFactory } from '../common';

/**
 * Hook to use a commands palette definition to edit a code block.
 */
export function useCodeBlockPaletteFactory(input: { nodeKey: string }): () => PaletteDef {
    const { nodeKey } = input;
    const editor = useEditor();
    const createLayoutGroup = useBlockLayoutGroupFactory({ nodeKey });
    const createBlockPalette = useBlockPaletteFactory({
        nodeKey,
        aiCompletion: true,
        createExtraGroups: () => {
            const block = editor.value.document.assertBlock(nodeKey);
            const data = block.data.toFrozenObject<BlockCodeData>();
            const hasTitle = typeof data.title === 'string';

            return [
                {
                    key: 'code',
                    commands: [
                        {
                            key: 'syntax',
                            title: 'Set syntax…',
                            icon: Icon.Code,
                            descendant: () => createCodeSyntaxesPalette(editor, nodeKey),
                        },
                    ],
                },
                {
                    key: 'code-options',
                    commands: [
                        {
                            key: 'with-line-numbers',
                            icon: Icon.CodeLineNumbers,
                            type: PaletteCommandType.SWITCH,
                            enabled: !!data.lineNumbers,
                            title: 'With line numbers',
                            trigger: () => {
                                editor.change((change) => {
                                    if (data.lineNumbers) {
                                        change.removeDataPathByKey(nodeKey, ['lineNumbers']);
                                    } else {
                                        change.setDataByKey(nodeKey, ['lineNumbers'], true);
                                    }
                                });

                                return createBlockPalette();
                            },
                        } satisfies PaletteCommandDef,
                        {
                            key: 'with-title',
                            icon: Icon.CodeCaption,
                            type: PaletteCommandType.SWITCH,
                            enabled: hasTitle,
                            title: 'With caption',
                            trigger: () => {
                                editor.change((change) => {
                                    if (hasTitle) {
                                        change.removeDataPathByKey(nodeKey, ['title']);
                                    } else {
                                        change.insertDataByKey(nodeKey, ['title'], '');
                                    }
                                });

                                return createBlockPalette();
                            },
                        } satisfies PaletteCommandDef,
                        {
                            key: 'with-wrap',
                            icon: Icon.CodeWrap,
                            type: PaletteCommandType.SWITCH,
                            enabled: data.overflow === 'wrap',
                            title: 'Wrap code',
                            trigger: () => {
                                editor.change((change) => {
                                    if (data.overflow === 'wrap') {
                                        change.removeDataPathByKey(nodeKey, ['overflow']);
                                    } else {
                                        change.setDataByKey(nodeKey, ['overflow'], 'wrap');
                                    }
                                });

                                return createBlockPalette();
                            },
                        } satisfies PaletteCommandDef,
                        {
                            key: 'with-expandable',
                            icon: Icon.BlockExpandable,
                            type: PaletteCommandType.SWITCH,
                            enabled: !!data.expandable,
                            title: 'Expandable',
                            trigger: () => {
                                editor.change((change) => {
                                    if (data.expandable) {
                                        change.removeDataPathByKey(nodeKey, ['expandable']);
                                    } else {
                                        change.setDataByKey(nodeKey, ['expandable'], true);
                                    }
                                });

                                return createBlockPalette();
                            },
                        } satisfies PaletteCommandDef,
                    ],
                },
                createLayoutGroup(),
            ].filter(filterOutNullable);
        },
    });

    return createBlockPalette;
}

/**
 * Get the command palette to pick a syntax for a code block.
 */
export function createCodeSyntaxesPalette(editor: EditorContainer, nodeKey: string): PaletteDef {
    const block = editor.value.document.assertBlock(nodeKey);
    const currentSyntax = block.data.get('syntax');

    return createPalette({
        search: { placeholder: 'Select a syntax…' },
        groups: [
            {
                key: 'syntaxes',
                commands: listCodeBlockSyntaxes().map((syntax) => {
                    return {
                        type: PaletteCommandType.CHECKBOX,
                        key: syntax.key,
                        selected: currentSyntax === syntax.key,
                        title: syntax.title,
                        trigger: () => {
                            editor.change((change) => {
                                change.setDataByKey(block.key, ['syntax'], syntax.key);
                            });
                        },
                    };
                }),
            },
        ],
    });
}
